/**
 *
 * A progressive image loading component that optimizes image delivery based on
 * network speed and viewport size to improve performance and user experience.
 *
 * Image Variant Sizes (from Rails ActiveStorage):
 * - thumbnail: 100px width (smallest, fastest to load)
 * - small: 360px width
 * - medium: 720px width
 * - large: 1080px width
 * - src: Original full-resolution image (highest quality)
 *
 * Loading Strategy:
 *
 * 1. Slow 3G Networks (2g, slow-2g):
 *    - Always loads thumbnail (100px) to minimize data usage
 *
 * 2. 3G Networks:
 *    - Viewport ≤ 640px (small): small variant (360px)
 *    - Viewport ≤ 1024px (medium): medium variant (720px)
 *    - Viewport > 1024px: large variant (1080px) - capped to save bandwidth
 *
 * 3. Fast Networks (4G+):
 *    - Viewport ≤ 640px (small): large variant (1080px) - high quality even on small screens
 *    - Viewport > 640px: original src (full resolution) - best quality for medium+ screens
 *
 * The component uses Intersection Observer to only load higher quality images
 * when they come into view, starting with the thumbnail as a placeholder.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { VIEWPORT_WIDTHS } from "./constants";

interface ImageVariants {
  large: string;
  medium: string;
  small: string;
  thumbnail: string;
}

interface ProgressiveImageProps {
  alt?: string;
  aspectRatio?: string;
  className?: string;
  loading?: "lazy" | "eager";
  showShimmer?: boolean;
  src: string;
  srcVariants?: ImageVariants;
}

interface NavigatorWithConnection extends Navigator {
  connection?: {
    effectiveType: string;
  };
}

const INTERSECTION_OBSERVER_OPTIONS = {
  threshold: 0.01,
  rootMargin: "50px",
} as const;

const NETWORK_EFFECTIVE_TYPE = {
  SLOW_2G: "slow-2g",
  TWO_G: "2g",
  THREE_G: "3g",
} as const;

export const ProgressiveImage = ({
  src,
  srcVariants,
  alt = t("post.cover_image_alt"),
  className = "",
  showShimmer = true,
  aspectRatio,
  loading = "lazy",
}: ProgressiveImageProps) => {
  const initialThumbnail = srcVariants?.thumbnail || src;

  const [imageSrc, setImageSrc] = useState<string>(initialThumbnail);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadedSrcRef = useRef<string>(initialThumbnail);
  const loadingImageRef = useRef<HTMLImageElement | null>(null);

  const isImageVariantsEmpty = isEmpty(srcVariants);
  const shouldShowLoadingState = isLoading && !hasError;

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
    setImageSrc(src);
  };

  const isNavigatorWithConnection = (
    navigator: Navigator,
  ): navigator is NavigatorWithConnection =>
    "connection" in navigator && navigator.connection !== undefined;

  const getSlow3gImageSrc = useCallback(
    (): string => srcVariants?.thumbnail || src,
    [srcVariants?.thumbnail, src],
  );

  const getThreeGImageSrc = useCallback(
    (width: number): string => {
      if (!srcVariants) return src;
      if (width <= VIEWPORT_WIDTHS.small) {
        return srcVariants.small;
      }
      if (width <= VIEWPORT_WIDTHS.medium) {
        return srcVariants.medium;
      }
      return srcVariants.large;
    },
    [srcVariants, src],
  );

  const getFastNetworkImageSrc = useCallback(
    (width: number): string => {
      if (!srcVariants) return src;
      if (width <= VIEWPORT_WIDTHS.small) {
        return srcVariants.large;
      }

      return src;
    },
    [src, srcVariants],
  );

  const getOptimalImageSrc = useCallback(
    (width: number): string => {
      if (isImageVariantsEmpty) return src;

      const connection = isNavigatorWithConnection(navigator)
        ? navigator.connection
        : undefined;

      const isSlow3gNetwork =
        connection &&
        (connection.effectiveType === NETWORK_EFFECTIVE_TYPE.TWO_G ||
          connection.effectiveType === NETWORK_EFFECTIVE_TYPE.SLOW_2G);

      const isThreeGNetwork =
        connection?.effectiveType === NETWORK_EFFECTIVE_TYPE.THREE_G;

      if (isSlow3gNetwork) {
        return getSlow3gImageSrc();
      }

      if (isThreeGNetwork) {
        return getThreeGImageSrc(width);
      }

      return getFastNetworkImageSrc(width);
    },
    [
      isImageVariantsEmpty,
      src,
      getSlow3gImageSrc,
      getThreeGImageSrc,
      getFastNetworkImageSrc,
    ],
  );

  const loadHigherQualityImage = useCallback(
    (targetSrc: string) => {
      if (targetSrc === loadedSrcRef.current || hasError) return;

      if (loadingImageRef.current) {
        loadingImageRef.current.onload = null;
        loadingImageRef.current.onerror = null;
        loadingImageRef.current.src = "";
      }

      setIsLoading(true);
      const img = new Image();
      loadingImageRef.current = img;
      img.src = targetSrc;

      img.onload = () => {
        setImageSrc(targetSrc);
        setIsLoading(false);
        loadedSrcRef.current = targetSrc;
        loadingImageRef.current = null;
      };

      img.onerror = () => {
        setHasError(true);
        setIsLoading(false);
        setImageSrc(src);
        loadingImageRef.current = null;
      };
    },
    [hasError, src],
  );

  const handleImageInView = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const viewportWidth = window.innerWidth;
          const optimalSrc = getOptimalImageSrc(viewportWidth);
          loadHigherQualityImage(optimalSrc);
        }
      }
    },
    [getOptimalImageSrc, loadHigherQualityImage],
  );

  useEffect(
    () => () => {
      if (loadingImageRef.current) {
        loadingImageRef.current.onload = null;
        loadingImageRef.current.onerror = null;
        loadingImageRef.current.src = "";
      }
    },
    [],
  );

  useEffect(() => {
    if (isImageVariantsEmpty || !imageRef.current) return;

    observerRef.current = new IntersectionObserver(
      handleImageInView,
      INTERSECTION_OBSERVER_OPTIONS,
    );

    observerRef.current.observe(imageRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [handleImageInView, isImageVariantsEmpty]);

  if (isImageVariantsEmpty) {
    return <img loading={loading} src={src} alt={alt} className={className} />;
  }

  return (
    <div
      className={classNames("relative overflow-hidden", className)}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {shouldShowLoadingState && (
        <>
          <div className="bg-secondary absolute inset-0" />
          {showShimmer && (
            <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent bg-[length:1000px_100%] bg-no-repeat" />
          )}
          <img
            loading={loading}
            src={srcVariants?.thumbnail || src}
            alt={alt}
            className="absolute inset-0 size-full scale-110 object-cover blur-md"
            aria-hidden="true"
          />
        </>
      )}

      <img
        loading={loading}
        ref={imageRef}
        src={imageSrc}
        onLoad={handleImageLoad}
        onError={handleImageError}
        alt={alt}
        className={classNames(
          "size-full object-cover transition-opacity duration-300",
          {
            "opacity-0": isLoading,
            "opacity-100": !isLoading,
          },
        )}
      />
    </div>
  );
};
